// Cognito Direct Authentication Service
// Uses AWS Cognito SDK directly instead of custom API calls

import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserSession, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { getCognitoUserPoolId, getCognitoClientId } from '../config/permanent-urls';

const cognitoIdpEndpointFromUserPoolId = (userPoolId: string): string | undefined => {
  const u = userPoolId.indexOf('_');
  if (u <= 0) {
    return undefined;
  }
  const region = userPoolId.slice(0, u);
  const isStd = /^[a-z]{2}-[a-z]+-\d+$/.test(region);
  const isGov = /^[a-z]{2}-gov-[a-z]+-\d+$/.test(region);
  if (!isStd && !isGov) {
    return undefined;
  }
  return `https://cognito-idp.${region}.amazonaws.com/`;
};

// Cognito configuration - Environment-aware
// Get User Pool ID and Client ID based on environment (dev/prod)
const USER_POOL_ID = getCognitoUserPoolId();
const CLIENT_ID = getCognitoClientId();
const COGNITO_IDP_ENDPOINT = cognitoIdpEndpointFromUserPoolId(USER_POOL_ID);

// Log once so you can verify against AWS Console (helps debug "client does not exist" 400)
if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  console.log('[Cognito] Using UserPoolId:', USER_POOL_ID, 'ClientId:', CLIENT_ID, 'Idp:', COGNITO_IDP_ENDPOINT ?? '(default)');
}

// Create Cognito User Pool (explicit Idp URL matches pool id region prefix)
const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: CLIENT_ID,
  ...(COGNITO_IDP_ENDPOINT ? { endpoint: COGNITO_IDP_ENDPOINT } : {}),
});

export interface CognitoProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  profile: CognitoProfile;
}

export interface NewPasswordChallengeResult {
  challengeName: 'NEW_PASSWORD_REQUIRED';
  cognitoUser: CognitoUser;
  email: string;
}

export interface RegistrationResult {
  needsVerification: boolean;
  user?: CognitoUser;
  email?: string;
  password?: string;
  authResult?: AuthResult;
  userExists?: boolean;
  isVerified?: boolean;
  redirectToLogin?: boolean;
}

export class CognitoAuthService {
  /**
   * Authenticate user with email and password
   */
  static async login(email: string, password: string): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result: CognitoUserSession) => {
          const accessToken = result.getAccessToken().getJwtToken();
          const idToken = result.getIdToken().getJwtToken();
          const refreshToken = result.getRefreshToken().getToken();

          // Extract user profile from ID token
          const payload = result.getIdToken().payload;
          const profile: CognitoProfile = {
            id: payload.sub,
            email: payload.email,
            firstName: payload.given_name || '',
            lastName: payload.family_name || '',
            isActive: true,
            createdAt: new Date(payload.auth_time * 1000).toISOString(),
            updatedAt: new Date().toISOString()
          };

          resolve({
            accessToken,
            idToken,
            refreshToken,
            profile
          });
        },
        onFailure: (err) => {
          const error = new Error(err.message || 'Authentication failed');
          // Preserve Cognito error code for better error handling
          if ((err as any).code) {
            (error as any).code = (err as any).code;
          }
          if ((err as any).name) {
            (error as any).name = (err as any).name;
          }
          reject(error);
        },
        newPasswordRequired: () => {
          const error = new Error('New password required');
          (error as any).code = 'NEW_PASSWORD_REQUIRED';
          (error as any).challengeName = 'NEW_PASSWORD_REQUIRED';
          (error as any).cognitoUser = cognitoUser;
          (error as any).email = email;
          reject(error);
        }
      });
    });
  }

  static async completeNewPassword(
    cognitoUser: CognitoUser,
    newPassword: string
  ): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (result: CognitoUserSession) => {
          const accessToken = result.getAccessToken().getJwtToken();
          const idToken = result.getIdToken().getJwtToken();
          const refreshToken = result.getRefreshToken().getToken();
          const payload = result.getIdToken().payload;
          const profile: CognitoProfile = {
            id: payload.sub,
            email: payload.email,
            firstName: payload.given_name || '',
            lastName: payload.family_name || '',
            isActive: true,
            createdAt: new Date(payload.auth_time * 1000).toISOString(),
            updatedAt: new Date().toISOString(),
          };

          resolve({
            accessToken,
            idToken,
            refreshToken,
            profile,
          });
        },
        onFailure: (err) => {
          const error = new Error(err.message || 'Failed to set new password');
          if ((err as any).code) {
            (error as any).code = (err as any).code;
          }
          reject(error);
        },
      });
    });
  }

  /**
   * Register new user or handle existing user
   */
  static async register(email: string, password: string, firstName: string, lastName: string): Promise<RegistrationResult> {
    return new Promise((resolve, reject) => {
      const attributeList = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName })
      ];

      userPool.signUp(email, password, attributeList, [], (err, result) => {
        if (err) {
          // Check if user already exists
          if ((err as any).code === 'UsernameExistsException') {
            // Try to login to check if user is verified
            this.login(email, password)
              .then((authResult) => {
                // User exists and is verified - redirect to login
                resolve({
                  needsVerification: false,
                  userExists: true,
                  isVerified: true,
                  redirectToLogin: true,
                  authResult: authResult
                });
              })
              .catch(() => {
                // User exists but not verified - resend verification
                this.resendVerificationCode(email)
                  .then(() => {
                    resolve({
                      userExists: true,
                      isVerified: false,
                      needsVerification: true,
                      email: email,
                      password: password
                    });
                  })
                  .catch(() => {
                    reject(new Error('Failed to resend verification code'));
                  });
              });
            return;
          }
          
          reject(new Error(err.message || 'Registration failed'));
          return;
        }

        if (result?.user) {
          // New user created - needs verification
          resolve({
            needsVerification: true,
            user: result.user,
            email: email,
            password: password,
            userExists: false,
            isVerified: false
          });
        }
      });
    });
  }

  /**
   * Resend verification code for existing unverified user
   */
  static async resendVerificationCode(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.resendConfirmationCode((err) => {
        if (err) {
          reject(new Error(err.message || 'Failed to resend verification code'));
          return;
        }
        
        resolve();
      });
    });
  }

  /**
   * Confirm user registration with verification code
   */
  static async confirmRegistration(user: CognitoUser, verificationCode: string, email: string, password: string): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      user.confirmRegistration(verificationCode, true, (confirmErr) => {
        if (confirmErr) {
          reject(new Error(confirmErr.message || 'Confirmation failed'));
          return;
        }

        // After confirmation, login the user
        this.login(email, password)
          .then(resolve)
          .catch(reject);
      });
    });
  }

  /**
   * Confirm registration for existing unverified user (without CognitoUser object)
   */
  static async confirmExistingUserRegistration(email: string, verificationCode: string, password: string): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.confirmRegistration(verificationCode, true, (confirmErr) => {
        if (confirmErr) {
          reject(new Error(confirmErr.message || 'Confirmation failed'));
          return;
        }

        // After confirmation, login the user
        this.login(email, password)
          .then(resolve)
          .catch(reject);
      });
    });
  }

  /**
   * Get current user session
   */
  static async getCurrentSession(): Promise<CognitoUserSession | null> {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.getSession((err: any, session: CognitoUserSession | null) => {
          if (err) {
            resolve(null);
          } else {
            resolve(session);
          }
        });
      } else {
        resolve(null);
      }
    });
  }

  /**
   * Logout user
   */
  static async logout(): Promise<void> {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
      resolve();
    });
  }

  /**
   * Refresh access token
   */
  static async refreshToken(): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.getSession((err: any, session: CognitoUserSession | null) => {
          if (err) {
            reject(new Error('No valid session'));
            return;
          }

          if (!session) {
            reject(new Error('No valid session'));
            return;
          }

          const refreshToken = session.getRefreshToken();
          cognitoUser.refreshSession(refreshToken, (refreshErr, refreshSession) => {
            if (refreshErr) {
              reject(new Error('Token refresh failed'));
              return;
            }

            const accessToken = refreshSession.getAccessToken().getJwtToken();
            const idToken = refreshSession.getIdToken().getJwtToken();
            const newRefreshToken = refreshSession.getRefreshToken().getToken();

            // Extract user profile from ID token
            const payload = refreshSession.getIdToken().payload;
            const profile: CognitoProfile = {
              id: payload.sub,
              email: payload.email,
              firstName: payload.given_name || '',
              lastName: payload.family_name || '',
              isActive: true,
              createdAt: new Date(payload.auth_time * 1000).toISOString(),
              updatedAt: new Date().toISOString()
            };

            resolve({
              accessToken,
              idToken,
              refreshToken: newRefreshToken,
              profile
            });
          });
        });
      } else {
        reject(new Error('No current user'));
      }
    });
  }
}

export default CognitoAuthService;
